import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Globe from 'globe.gl';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const API_URL = process.env.REACT_APP_SELLERS_API_URL || 'https://mapazon.aihello.com/api/sellers';
const MAX_CACHED_POINTS = 480;
const MAX_SELLERS_PER_RESPONSE = 220;
const SELLERS_REQUEST_LIMIT = 200;
const VIEW_FETCH_DEBOUNCE_MS = 1100;
const MAX_DEVICE_PIXEL_RATIO = 1.25;
const DEFAULT_VIEW = { lat: 37.0902, lng: -95.7129, altitude: 1.5 };
const CURRENT_LOCATION_VIEW_ALTITUDE = 0.9;
const GEOLOCATION_TIMEOUT_MS = 5000;
const DEFAULT_MAX_VALUES = {
  revenue: 1000000,
  totalAsins: 1000,
  topAsins: 100,
  lifetimeReviews: 10000
};

const RANGE_FILTERS = [
  { key: 'revenue', label: 'Revenue', valueLabel: value => formatCurrency(value) },
  { key: 'totalAsins', label: 'Total ASINs', valueLabel: value => formatNumber(value) },
  { key: 'topAsins', label: 'Top ASINs', valueLabel: value => formatNumber(value) },
  { key: 'lifetimeReviews', label: 'Lifetime Reviews', valueLabel: value => formatNumber(value) }
];

function createDefaultFilterState() {
  return {
    values: {
      revenue: null,
      totalAsins: null,
      topAsins: null,
      lifetimeReviews: null,
      mainCategories: [],
      subcategories: []
    },
    enabled: {
      revenue: false,
      totalAsins: false,
      topAsins: false,
      lifetimeReviews: false
    }
  };
}

function debounce(fn, delay = 450) {
  let timer;

  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };

  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value) {
  const parsed = toNumber(value, null);

  if (parsed === null) {
    return '--';
  }

  return `$${Math.round(parsed).toLocaleString()}`;
}

function formatNumber(value) {
  const parsed = toNumber(value, null);
  return parsed === null ? '--' : Math.round(parsed).toLocaleString();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getZoomFromAltitude(altitude) {
  if (altitude > 3.5) return 0.5;
  if (altitude > 2.5) return 1.0;
  if (altitude > 1.5) return 1.5;
  if (altitude > 1.0) return 2.0;
  if (altitude > 0.5) return 3.0;
  if (altitude > 0.26) return 4.0;

  return 5.0;
}

function computeLngExtent(lngs) {
  if (lngs.length === 0) {
    return null;
  }

  if (lngs.length === 1) {
    return { minLng: lngs[0], maxLng: lngs[0] };
  }

  const sorted = [...lngs].sort((a, b) => a - b);
  let maxInternalGap = 0;
  let gapStart = sorted[0];
  let gapEnd = sorted[1];

  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i] - sorted[i - 1];
    if (gap > maxInternalGap) {
      maxInternalGap = gap;
      gapStart = sorted[i - 1];
      gapEnd = sorted[i];
    }
  }

  const wrapGap = (sorted[0] + 360) - sorted[sorted.length - 1];

  if (maxInternalGap > wrapGap) {
    return { minLng: gapEnd, maxLng: gapStart };
  }

  return { minLng: sorted[0], maxLng: sorted[sorted.length - 1] };
}

function getGeoCoordsAtScreen(globe, screenX, screenY) {
  if (!globe || typeof globe.toGlobeCoords !== 'function') {
    return null;
  }
  return globe.toGlobeCoords(screenX, screenY);
}

function getFallbackBounds(view) {
  const lat = toNumber(view?.lat, DEFAULT_VIEW.lat);
  const lng = toNumber(view?.lng, DEFAULT_VIEW.lng);
  const altitude = toNumber(view?.altitude, DEFAULT_VIEW.altitude);
  const latSpan = clamp(altitude * 42, 8, 85);
  const lngSpan = clamp(altitude * 64, 12, 170);

  let minLng = lng - lngSpan;
  let maxLng = lng + lngSpan;

  if (lngSpan * 2 >= 360) {
    minLng = -180;
    maxLng = 180;
  } else {
    if (minLng < -180) minLng += 360;
    if (maxLng > 180) maxLng -= 360;
  }

  return {
    minLat: clamp(lat - latSpan, -90, 90),
    maxLat: clamp(lat + latSpan, -90, 90),
    minLng,
    maxLng
  };
}

function buildActiveFilters(filterState) {
  const filters = {};

  Object.entries(filterState.enabled).forEach(([key, isEnabled]) => {
    const value = filterState.values[key];

    if (isEnabled && Array.isArray(value)) {
      filters[key] = value;
    }
  });

  ['mainCategories', 'subcategories'].forEach(key => {
    const value = filterState.values[key];

    if (Array.isArray(value) && value.length > 0) {
      filters[key] = value;
    }
  });

  return filters;
}

function getPointKey(point) {
  return [
    point.sellerId || point.amazonSellerId || point.sellerName || 'seller',
    point.lat.toFixed(5),
    point.lng.toFixed(5)
  ].join(':');
}

function mapSellerToPoint(seller) {
  const location = seller.location || {};
  const lat = toNumber(location.latitude, null);
  const lng = toNumber(location.longitude, null);

  if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const monthlyRevenueUSD = toNumber(seller.monthlyRevenueUSD, 0);
  const estimatedSalesUSD = toNumber(seller.estimatedSalesUSD, 0);
  const heatValue = Math.max(monthlyRevenueUSD, estimatedSalesUSD, 1);
  const point = {
    sellerId: seller.sellerId || seller.documentId || seller.amazonSellerId,
    documentId: seller.documentId,
    sellerName: seller.sellerName || seller.businessName || 'Unknown seller',
    businessName: seller.businessName,
    amazonSellerId: seller.amazonSellerId,
    lat,
    lng,
    country: location.country,
    city: location.city,
    state: location.state,
    streetAddress: location.streetAddress,
    postalCode: location.postalCode,
    brandName: seller.brand?.brandName,
    sellerType: seller.sellerType,
    category: seller.category || {},
    growth: seller.growth || {},
    reviews: seller.reviews || {},
    productStats: seller.productStats || {},
    status: seller.status || {},
    monthlyRevenueUSD,
    estimatedSalesUSD,
    averagePriceUSD: seller.averagePriceUSD,
    fbaPercentage: toNumber(seller.fbaPercentage, 0),
    heatWeight: Math.max(1, Math.log10(heatValue + 10)),
    lastUpdated: seller.lastUpdated
  };

  point.key = getPointKey(point);
  return point;
}

function distanceKm(a, b) {
  const earthRadiusKm = 6371;
  const lat1 = a.lat * Math.PI / 180;
  const lat2 = b.lat * Math.PI / 180;
  const deltaLat = (b.lat - a.lat) * Math.PI / 180;
  const deltaLng = (b.lng - a.lng) * Math.PI / 180;
  const haversine = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

const CLUSTER_CELL_TARGET_PX = 80;

function getClusterCellSizeDeg(pxPerDeg) {
  if (!Number.isFinite(pxPerDeg) || pxPerDeg <= 0) {
    return 0.5;
  }
  return clamp(CLUSTER_CELL_TARGET_PX / pxPerDeg, 0.0005, 12);
}

function clusterPoints(points, cellSize) {
  if (cellSize <= 0 || points.length === 0) {
    return [];
  }

  const cells = new Map();

  for (const point of points) {
    const cellLat = Math.floor(point.lat / cellSize);
    const cellLng = Math.floor(point.lng / cellSize);
    const cellKey = `${cellLat}:${cellLng}`;
    let cell = cells.get(cellKey);

    if (!cell) {
      cell = {
        cellKey,
        points: [],
        cityCounts: new Map(),
        sumLat: 0,
        sumLng: 0,
        totalRevenue: 0
      };
      cells.set(cellKey, cell);
    }

    cell.points.push(point);
    cell.sumLat += point.lat;
    cell.sumLng += point.lng;
    cell.totalRevenue += point.monthlyRevenueUSD || 0;
    const placeKey = point.city || point.state || point.country;

    if (placeKey) {
      cell.cityCounts.set(placeKey, (cell.cityCounts.get(placeKey) || 0) + 1);
    }
  }

  return Array.from(cells.values()).map(cell => {
    const count = cell.points.length;
    let dominantPlace = '';
    let bestCount = 0;

    cell.cityCounts.forEach((value, place) => {
      if (value > bestCount) {
        bestCount = value;
        dominantPlace = place;
      }
    });

    return {
      key: `cluster:${cell.cellKey}`,
      lat: cell.sumLat / count,
      lng: cell.sumLng / count,
      count,
      city: dominantPlace,
      totalRevenue: cell.totalRevenue,
      points: cell.points
    };
  });
}

function getClusterDotRadius(cluster) {
  if (cluster.count === 1) {
    return 0.22;
  }
  return clamp(0.3 + Math.sqrt(cluster.count) * 0.18, 0.32, 1.6);
}

function getClusterDotRadiusBounded(cluster, maxDeg) {
  const base = getClusterDotRadius(cluster);
  return Number.isFinite(maxDeg) ? Math.min(base, maxDeg) : base;
}

function computePixelsPerDegree(globe) {
  if (!globe || typeof globe.getScreenCoords !== 'function' || typeof globe.pointOfView !== 'function') {
    return 0;
  }

  const view = globe.pointOfView();
  const lat = toNumber(view?.lat, 0);
  const lng = toNumber(view?.lng, 0);
  const sampleLat = lat > 89 ? lat - 1 : lat + 1;
  const a = globe.getScreenCoords(lat, lng);
  const b = globe.getScreenCoords(sampleLat, lng);

  if (!a || !b || !Number.isFinite(a.x) || !Number.isFinite(b.x)) {
    return 0;
  }

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function getClusterLabelSize(cluster) {
  if (cluster.count === 1) {
    return 0.3;
  }
  return clamp(0.32 + Math.log2(cluster.count) * 0.04, 0.32, 0.55);
}

function getClusterLabelSizeBounded(cluster, pxPerDeg) {
  if (!Number.isFinite(pxPerDeg) || pxPerDeg <= 0) {
    return getClusterLabelSize(cluster);
  }
  const targetPx = clamp(14 + Math.log2(Math.max(cluster.count, 2)) * 2, 14, 22);
  return targetPx / pxPerDeg;
}

function getClusterLabelText(cluster) {
  if (cluster.count === 1) {
    const point = cluster.points[0];
    return point.sellerName || point.city || '';
  }
  if (cluster.city) {
    return `${cluster.city} · ${cluster.count}`;
  }
  return `${cluster.count} sellers`;
}

function getClusterColor(cluster) {
  if (cluster.count >= 25) return 'rgba(220, 38, 38, 0.92)';
  if (cluster.count >= 10) return 'rgba(217, 119, 6, 0.94)';
  if (cluster.count >= 3) return 'rgba(232, 122, 24, 0.92)';
  return 'rgba(15, 118, 110, 0.92)';
}

function combineClusters(a, b) {
  const totalCount = a.count + b.count;
  const lat = (a.lat * a.count + b.lat * b.count) / totalCount;
  const lng = (a.lng * a.count + b.lng * b.count) / totalCount;
  const points = a.points.concat(b.points);
  const totalRevenue = (a.totalRevenue || 0) + (b.totalRevenue || 0);
  const cityCounts = new Map();

  for (const point of points) {
    const placeKey = point.city || point.state || point.country;
    if (placeKey) {
      cityCounts.set(placeKey, (cityCounts.get(placeKey) || 0) + 1);
    }
  }

  let dominantPlace = '';
  let bestCount = 0;
  cityCounts.forEach((value, place) => {
    if (value > bestCount) {
      bestCount = value;
      dominantPlace = place;
    }
  });

  return {
    key: `merged:${a.key}|${b.key}`,
    lat,
    lng,
    count: totalCount,
    city: dominantPlace,
    totalRevenue,
    points
  };
}

function getMergeRadiusPx(cluster) {
  return 50 + Math.sqrt(cluster.count) * 8;
}

function mergeOverlappingClusters(clusters, projectToScreen, width, height, pxPerDeg, maxDotRadiusDeg) {
  if (clusters.length <= 1 || !width || !height) {
    return clusters;
  }

  const labelMarginPx = 20;

  const margin = 120;
  const enriched = clusters.map(cluster => {
    const screen = projectToScreen(cluster.lat, cluster.lng);
    const visible = screen
      && Number.isFinite(screen.x)
      && Number.isFinite(screen.y)
      && screen.x >= -margin
      && screen.x <= width + margin
      && screen.y >= -margin
      && screen.y <= height + margin;
    return { cluster, screen, visible };
  });

  const offscreen = [];
  const visible = [];
  for (const entry of enriched) {
    (entry.visible ? visible : offscreen).push(entry);
  }

  visible.sort((a, b) => {
    if (b.cluster.count !== a.cluster.count) {
      return b.cluster.count - a.cluster.count;
    }
    return (b.cluster.totalRevenue || 0) - (a.cluster.totalRevenue || 0);
  });

  const used = new Array(visible.length).fill(false);
  const merged = [];

  for (let i = 0; i < visible.length; i++) {
    if (used[i]) continue;
    used[i] = true;

    let combined = visible[i].cluster;
    let combinedX = visible[i].screen.x;
    let combinedY = visible[i].screen.y;

    let didMerge = true;
    while (didMerge) {
      didMerge = false;
      for (let j = i + 1; j < visible.length; j++) {
        if (used[j]) continue;
        const other = visible[j];
        const dx = combinedX - other.screen.x;
        const dy = combinedY - other.screen.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        let threshold;
        if (pxPerDeg > 0) {
          const radiusAPx = getClusterDotRadiusBounded(combined, maxDotRadiusDeg) * pxPerDeg;
          const radiusBPx = getClusterDotRadiusBounded(other.cluster, maxDotRadiusDeg) * pxPerDeg;
          threshold = radiusAPx + radiusBPx + labelMarginPx;
        } else {
          threshold = getMergeRadiusPx(combined);
        }

        if (dist < threshold) {
          used[j] = true;
          combined = combineClusters(combined, other.cluster);
          const reprojected = projectToScreen(combined.lat, combined.lng);
          if (reprojected && Number.isFinite(reprojected.x) && Number.isFinite(reprojected.y)) {
            combinedX = reprojected.x;
            combinedY = reprojected.y;
          }
          didMerge = true;
        }
      }
    }

    merged.push(combined);
  }

  for (const entry of offscreen) {
    merged.push(entry.cluster);
  }

  return merged;
}

const SELLER_MARKER_SVG = `
<svg width="22" height="28" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg" style="display:block;filter:drop-shadow(0 2px 3px rgba(15,23,42,0.45));">
  <path d="M12 1.5C6.75 1.5 2.5 5.75 2.5 11c0 6.75 9.5 17.5 9.5 17.5s9.5-10.75 9.5-17.5c0-5.25-4.25-9.5-9.5-9.5z" fill="#0f766e" stroke="#ffffff" stroke-width="1.5"/>
  <circle cx="12" cy="10.5" r="3.5" fill="#ffffff"/>
</svg>
`;

function createSellerMarkerElement(cluster, onClick) {
  const wrapper = document.createElement('div');
  wrapper.className = 'seller-marker';
  wrapper.style.cssText = 'width:22px;height:28px;pointer-events:auto;cursor:pointer;transform:translate(-50%,-100%);transform-origin:50% 100%;transition:transform 0.18s ease;';
  wrapper.innerHTML = SELLER_MARKER_SVG;

  const seller = cluster.points && cluster.points[0];
  if (seller && seller.sellerName) {
    wrapper.title = seller.sellerName;
  }

  wrapper.addEventListener('mouseenter', () => {
    wrapper.style.transform = 'translate(-50%,-100%) scale(1.18)';
  });
  wrapper.addEventListener('mouseleave', () => {
    wrapper.style.transform = 'translate(-50%,-100%)';
  });
  wrapper.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick(cluster, event);
  });

  return wrapper;
}

function buildRequestKey({ bounds, filters, zoom }) {
  const latSpan = Math.max(bounds.maxLat - bounds.minLat, 0.0001);
  const lngSpanRaw = bounds.minLng <= bounds.maxLng
    ? bounds.maxLng - bounds.minLng
    : (180 - bounds.minLng) + (bounds.maxLng + 180);
  const lngSpan = Math.max(lngSpanRaw, 0.0001);
  const latStep = latSpan * 0.05;
  const lngStep = lngSpan * 0.05;

  return JSON.stringify({
    minLat: Math.round(bounds.minLat / latStep) * latStep,
    maxLat: Math.round(bounds.maxLat / latStep) * latStep,
    minLng: Math.round(bounds.minLng / lngStep) * lngStep,
    maxLng: Math.round(bounds.maxLng / lngStep) * lngStep,
    filters,
    zoom: Number(zoom.toFixed(1))
  });
}

const SellerGlobeApp = () => {
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const pointsRef = useRef([]);
  const pointMapRef = useRef(new Map());
  const filterStateRef = useRef(createDefaultFilterState());
  const fetchRef = useRef(null);
  const filterGenerationRef = useRef(0);
  const filterEffectReadyRef = useRef(false);
  const lastRequestKeyRef = useRef('');
  const renderFrameRef = useRef(null);
  const requestControllerRef = useRef(null);
  const popoverRef = useRef(null);
  const renderRef = useRef(null);
  const clusterClickRef = useRef(null);
  const expandedSellerKeysRef = useRef(new Set());
  const maxDotRadiusDegRef = useRef(Infinity);
  const pxPerDegRef = useRef(0);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterState, setFilterState] = useState(createDefaultFilterState);
  const [maxValues, setMaxValues] = useState(DEFAULT_MAX_VALUES);
  const [availableCategories, setAvailableCategories] = useState({
    mainCategories: [],
    subcategories: []
  });
  const [cachedPointCount, setCachedPointCount] = useState(0);
  const [loadState, setLoadState] = useState({
    loading: false,
    error: null,
    loadedAt: null
  });
  const [popover, setPopover] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);

  const activeFilterCount = useMemo(() => {
    const activeFilters = buildActiveFilters(filterState);
    return Object.keys(activeFilters).length;
  }, [filterState]);

  const refreshMetadata = useCallback(() => {
    const points = pointsRef.current;
    const categories = new Set();
    const subcategories = new Set();
    const nextMaxValues = { ...DEFAULT_MAX_VALUES };

    for (let i = 0; i < points.length; i++) {
      const point = points[i];

      if (point.category.mainCategory) {
        categories.add(point.category.mainCategory);
      }

      if (point.category.subCategory) {
        subcategories.add(point.category.subCategory);
      }

      const revenue = point.monthlyRevenueUSD || 0;
      const totalAsins = point.productStats.totalAsins || 0;
      const topAsins = point.productStats.topAsins || 0;
      const lifetimeReviews = point.reviews.totalLifetime || 0;

      if (revenue > nextMaxValues.revenue) nextMaxValues.revenue = revenue;
      if (totalAsins > nextMaxValues.totalAsins) nextMaxValues.totalAsins = totalAsins;
      if (topAsins > nextMaxValues.topAsins) nextMaxValues.topAsins = topAsins;
      if (lifetimeReviews > nextMaxValues.lifetimeReviews) nextMaxValues.lifetimeReviews = lifetimeReviews;
    }

    const sortedMain = Array.from(categories).sort();
    const sortedSub = Array.from(subcategories).sort();

    setAvailableCategories(prev => {
      if (
        prev.mainCategories.length === sortedMain.length &&
        prev.subcategories.length === sortedSub.length &&
        prev.mainCategories.every((value, index) => value === sortedMain[index]) &&
        prev.subcategories.every((value, index) => value === sortedSub[index])
      ) {
        return prev;
      }

      return { mainCategories: sortedMain, subcategories: sortedSub };
    });

    setMaxValues(prev => {
      if (
        prev.revenue === nextMaxValues.revenue &&
        prev.totalAsins === nextMaxValues.totalAsins &&
        prev.topAsins === nextMaxValues.topAsins &&
        prev.lifetimeReviews === nextMaxValues.lifetimeReviews
      ) {
        return prev;
      }

      return nextMaxValues;
    });

    setCachedPointCount(prev => (prev === points.length ? prev : points.length));
  }, []);

  const renderGlobePoints = useCallback(() => {
    const globe = globeRef.current;

    if (!globe) {
      return;
    }

    cancelAnimationFrame(renderFrameRef.current);
    renderFrameRef.current = requestAnimationFrame(() => {
      const liveGlobe = globeRef.current;

      if (!liveGlobe) {
        return;
      }

      const container = containerRef.current;
      const width = container?.clientWidth || 0;
      const height = container?.clientHeight || 0;
      const pxPerDeg = computePixelsPerDegree(liveGlobe);
      const maxDotRadiusDeg = pxPerDeg > 0
        ? (Math.min(width, height) * 0.10) / pxPerDeg
        : Infinity;
      maxDotRadiusDegRef.current = maxDotRadiusDeg;
      pxPerDegRef.current = pxPerDeg;
      const cellSize = getClusterCellSizeDeg(pxPerDeg);
      const expandedKeys = expandedSellerKeysRef.current;
      const cachedPoints = pointsRef.current;
      const clusterablePoints = expandedKeys.size > 0
        ? cachedPoints.filter(point => !expandedKeys.has(point.key))
        : cachedPoints;
      const explodedPoints = expandedKeys.size > 0
        ? cachedPoints.filter(point => expandedKeys.has(point.key))
        : [];
      const gridClusters = clusterPoints(clusterablePoints, cellSize);
      const clusters = (width && height)
        ? mergeOverlappingClusters(
            gridClusters,
            (lat, lng) => liveGlobe.getScreenCoords(lat, lng),
            width,
            height,
            pxPerDeg,
            maxDotRadiusDeg
          )
        : gridClusters;
      const singles = [];
      const aggregates = [];

      for (const cluster of clusters) {
        if (cluster.count === 1) {
          singles.push(cluster);
        } else {
          aggregates.push(cluster);
        }
      }

      for (const point of explodedPoints) {
        singles.push({
          key: `expanded:${point.key}`,
          lat: point.lat,
          lng: point.lng,
          count: 1,
          city: point.city || point.state || point.country || '',
          totalRevenue: point.monthlyRevenueUSD || 0,
          points: [point]
        });
      }

      liveGlobe
        .htmlElementsData(singles)
        .labelsData(aggregates);
    });
  }, []);

  const syncGlobeData = useCallback(() => {
    renderGlobePoints();
    refreshMetadata();
  }, [renderGlobePoints, refreshMetadata]);

  const clearGlobeData = useCallback(() => {
    pointsRef.current = [];
    pointMapRef.current = new Map();
    lastRequestKeyRef.current = '';
    setPopover(null);
    setPopoverPosition(null);
    syncGlobeData();
  }, [syncGlobeData]);

  const positionPopoverAt = useCallback((lat, lng) => {
    const globe = globeRef.current;
    const container = containerRef.current;

    if (!globe || !container) {
      return;
    }

    const coords = globe.getScreenCoords(lat, lng);
    const rect = container.getBoundingClientRect();
    const left = clamp(coords.x, 12, Math.max(12, rect.width - 430));
    const top = clamp(coords.y, 12, Math.max(12, rect.height - 260));

    setPopoverPosition(prev => (
      prev && prev.left === left && prev.top === top ? prev : { left, top }
    ));
  }, []);

  const updateCurrentPopoverPosition = useCallback(() => {
    const currentPopover = popoverRef.current;

    if (!currentPopover) {
      return;
    }

    positionPopoverAt(currentPopover.lat, currentPopover.lng);
  }, [positionPopoverAt]);

  const openSellerPopover = useCallback((seller) => {
    const nextPopover = {
      type: 'seller',
      seller,
      lat: seller.lat,
      lng: seller.lng
    };

    popoverRef.current = nextPopover;
    setPopover(nextPopover);
    positionPopoverAt(seller.lat, seller.lng);
  }, [positionPopoverAt]);

  const getNearbySellers = useCallback((coords, limit = 8) => {
    return pointsRef.current
      .map(point => ({
        ...point,
        distance: distanceKm({ lat: coords.lat, lng: coords.lng }, point)
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);
  }, []);

  const openRegionPopover = useCallback((coords) => {
    const nearby = getNearbySellers(coords);
    const totalRevenue = nearby.reduce((sum, point) => sum + (point.monthlyRevenueUSD || 0), 0);
    const nextPopover = {
      type: 'region',
      lat: coords.lat,
      lng: coords.lng,
      nearby,
      totalRevenue
    };

    popoverRef.current = nextPopover;
    setPopover(nextPopover);
    positionPopoverAt(coords.lat, coords.lng);
  }, [getNearbySellers, positionPopoverAt]);

  const handleClusterClick = useCallback((cluster, event) => {
    event?.stopPropagation?.();

    if (cluster.count === 1) {
      openSellerPopover(cluster.points[0]);
      return;
    }

    const expandedKeys = expandedSellerKeysRef.current;
    for (const point of cluster.points) {
      expandedKeys.add(point.key);
    }
    renderRef.current?.();
  }, [openSellerPopover]);

  const mergeSellerPoints = useCallback((sellers, { reset = false } = {}) => {
    if (reset) {
      pointsRef.current = [];
      pointMapRef.current = new Map();
    }

    const boundedSellers = Array.isArray(sellers)
      ? sellers.slice(0, MAX_SELLERS_PER_RESPONSE)
      : [];

    boundedSellers
      .map(mapSellerToPoint)
      .filter(Boolean)
      .forEach(point => {
        pointMapRef.current.set(point.key, point);
      });

    const points = Array.from(pointMapRef.current.values()).slice(-MAX_CACHED_POINTS);
    pointsRef.current = points;
    pointMapRef.current = new Map(points.map(point => [point.key, point]));
    syncGlobeData();
  }, [syncGlobeData]);

  const calculateVisibleBounds = useCallback((globe) => {
    if (!globe) {
      return null;
    }

    const renderer = globe.renderer();
    const { clientWidth: width, clientHeight: height } = renderer?.domElement || {};

    if (!width || !height) {
      return null;
    }

    const xSamples = [0, width / 4, width / 2, width * 0.75, width - 1];
    const ySamples = [0, height / 4, height / 2, height * 0.75, height - 1];
    const coords = xSamples
      .flatMap(x => ySamples.map(y => getGeoCoordsAtScreen(globe, x, y)))
      .filter(point => point && Number.isFinite(point.lat) && Number.isFinite(point.lng));

    if (coords.length === 0) {
      return null;
    }

    const lats = coords.map(point => point.lat);
    const lngExtent = computeLngExtent(coords.map(point => point.lng));

    if (!lngExtent) {
      return null;
    }

    return {
      minLat: Math.max(-90, Math.min(...lats)),
      maxLat: Math.min(90, Math.max(...lats)),
      minLng: clamp(lngExtent.minLng, -180, 180),
      maxLng: clamp(lngExtent.maxLng, -180, 180)
    };
  }, []);

  const loadSellersForCurrentView = useCallback(async ({ reset = false } = {}) => {
    const globe = globeRef.current;

    if (!globe) {
      return;
    }

    const view = globe.pointOfView();
    const bounds = calculateVisibleBounds(globe) || getFallbackBounds(view);
    const generation = filterGenerationRef.current;
    const zoom = getZoomFromAltitude(view.altitude);
    const filters = buildActiveFilters(filterStateRef.current);
    const requestKey = buildRequestKey({ bounds, filters, zoom });

    if (!reset && requestKey === lastRequestKeyRef.current) {
      return;
    }

    const requestBody = {
      center: {
        lat: view.lat,
        lng: view.lng
      },
      zoom,
      bounds,
      filters,
      limit: SELLERS_REQUEST_LIMIT
    };
    const controller = new AbortController();

    requestControllerRef.current?.abort();
    requestControllerRef.current = controller;
    lastRequestKeyRef.current = requestKey;

    setLoadState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Seller API returned ${response.status}`);
      }

      const payload = await response.json();

      if (generation !== filterGenerationRef.current || requestControllerRef.current !== controller) {
        return;
      }

      mergeSellerPoints(Array.isArray(payload.sellers) ? payload.sellers : [], { reset });
      setLoadState({
        loading: false,
        error: null,
        loadedAt: new Date()
      });
    } catch (error) {
      if (error.name === 'AbortError' || generation !== filterGenerationRef.current || requestControllerRef.current !== controller) {
        return;
      }

      setLoadState({
        loading: false,
        error: error.message,
        loadedAt: null
      });
    } finally {
      if (requestControllerRef.current === controller) {
        requestControllerRef.current = null;
      }
    }
  }, [calculateVisibleBounds, mergeSellerPoints]);

  useEffect(() => {
    fetchRef.current = loadSellersForCurrentView;
  }, [loadSellersForCurrentView]);

  useEffect(() => {
    renderRef.current = renderGlobePoints;
  }, [renderGlobePoints]);

  useEffect(() => {
    clusterClickRef.current = handleClusterClick;
  }, [handleClusterClick]);

  useEffect(() => {
    filterStateRef.current = filterState;

    if (!filterEffectReadyRef.current) {
      filterEffectReadyRef.current = true;
      return undefined;
    }

    const timer = setTimeout(() => {
      filterGenerationRef.current += 1;
      clearGlobeData();
      fetchRef.current?.({ reset: true });
    }, 280);

    return () => clearTimeout(timer);
  }, [clearGlobeData, filterState]);

  useEffect(() => {
    popoverRef.current = popover;

    if (globeRef.current && pointsRef.current.length > 0) {
      renderGlobePoints();
    }
  }, [popover, renderGlobePoints]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const globe = Globe({
      rendererConfig: {
        antialias: false,
        alpha: true,
        powerPreference: 'high-performance'
      }
    })(containerRef.current)
      .backgroundColor('#000011')
      .backgroundImageUrl('//cdn.jsdelivr.net/npm/three-globe/example/img/night-sky.png')
      .globeCurvatureResolution(32)
      .globeTileEngineUrl((x, y, l) => `https://tile.openstreetmap.org/${l}/${x}/${y}.png`)
      .showAtmosphere(true)
      .atmosphereColor('#8ecae6')
      .atmosphereAltitude(0.08)
      .heatmapsData([])
      .hexBinPointsData([])
      .hexBinMerge(true)
      .hexTransitionDuration(0)
      .htmlElementsData([])
      .htmlLat('lat')
      .htmlLng('lng')
      .htmlAltitude(0.005)
      .htmlElement(cluster => createSellerMarkerElement(
        cluster,
        (c, event) => clusterClickRef.current?.(c, event)
      ))
      .htmlTransitionDuration(0)
      .labelsData([])
      .labelLat('lat')
      .labelLng('lng')
      .labelText(getClusterLabelText)
      .labelSize(cluster => getClusterLabelSizeBounded(cluster, pxPerDegRef.current))
      .labelDotRadius(cluster => getClusterDotRadiusBounded(cluster, maxDotRadiusDegRef.current))
      .labelAltitude(0.006)
      .labelColor(getClusterColor)
      .labelResolution(2)
      .labelsTransitionDuration(0)
      .onLabelClick((label, event) => {
        clusterClickRef.current?.(label, event);
      })
      .showPointerCursor((obj) => !!obj);

    globe.controls().autoRotate = false;
    globe.pointOfView(DEFAULT_VIEW, 0);
    globe.lights().find(light => light.type === 'DirectionalLight')?.position.set(1, 1, 1);
    globeRef.current = globe;

    const resizeGlobe = () => {
      const { clientWidth, clientHeight } = containerRef.current || {};
      const renderer = globe.renderer();

      renderer?.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO));

      if (clientWidth && clientHeight) {
        globe.width(clientWidth).height(clientHeight);
      }

      updateCurrentPopoverPosition();
    };

    const debouncedLoad = debounce(() => {
      fetchRef.current?.({ reset: false });
    }, VIEW_FETCH_DEBOUNCE_MS);
    let initialLoadTimer;
    let disposed = false;

    let gestureStartView = null;

    const handleControlsStart = () => {
      const view = globeRef.current?.pointOfView();
      gestureStartView = view
        ? { lat: view.lat, lng: view.lng, altitude: view.altitude }
        : null;
    };

    const handleControlsChange = () => {
      updateCurrentPopoverPosition();
    };

    const handleControlsEnd = () => {
      updateCurrentPopoverPosition();
      const currentView = globeRef.current?.pointOfView();
      const wasGesture = gestureStartView && currentView && (
        Math.abs(gestureStartView.lat - currentView.lat) > 1e-6
        || Math.abs(gestureStartView.lng - currentView.lng) > 1e-6
        || Math.abs(gestureStartView.altitude - currentView.altitude) > 1e-7
      );
      gestureStartView = null;

      if (!wasGesture) {
        return;
      }

      if (expandedSellerKeysRef.current.size > 0) {
        expandedSellerKeysRef.current = new Set();
      }
      renderRef.current?.();
      debouncedLoad();
    };

    const finishInitialLoad = (view, animationMs) => {
      if (disposed) {
        return;
      }

      globe.pointOfView(view, animationMs);
      initialLoadTimer = setTimeout(() => {
        fetchRef.current?.({ reset: true });
      }, animationMs + 150);
    };

    const loadFromDefaultView = () => {
      finishInitialLoad(DEFAULT_VIEW, 0);
    };

    resizeGlobe();
    window.addEventListener('resize', resizeGlobe);
    globe.controls().addEventListener('start', handleControlsStart);
    globe.controls().addEventListener('change', handleControlsChange);
    globe.controls().addEventListener('end', handleControlsEnd);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          finishInitialLoad({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            altitude: CURRENT_LOCATION_VIEW_ALTITUDE
          }, 900);
        },
        loadFromDefaultView,
        {
          enableHighAccuracy: false,
          maximumAge: 10 * 60 * 1000,
          timeout: GEOLOCATION_TIMEOUT_MS
        }
      );
    } else {
      loadFromDefaultView();
    }

    return () => {
      disposed = true;
      clearTimeout(initialLoadTimer);
      cancelAnimationFrame(renderFrameRef.current);
      requestControllerRef.current?.abort();
      debouncedLoad.cancel();
      window.removeEventListener('resize', resizeGlobe);
      globe.controls().removeEventListener('start', handleControlsStart);
      globe.controls().removeEventListener('change', handleControlsChange);
      globe.controls().removeEventListener('end', handleControlsEnd);
      globe._destructor?.();
      globeRef.current = null;
    };
  }, [updateCurrentPopoverPosition]);

  const enableFilter = useCallback((key, sliderMax) => {
    setFilterState(prev => ({
      ...prev,
      enabled: {
        ...prev.enabled,
        [key]: true
      },
      values: {
        ...prev.values,
        [key]: [0, sliderMax]
      }
    }));
  }, []);

  const disableFilter = useCallback((key) => {
    setFilterState(prev => ({
      ...prev,
      enabled: {
        ...prev.enabled,
        [key]: false
      },
      values: {
        ...prev.values,
        [key]: null
      }
    }));
  }, []);

  const updateRangeFilter = useCallback((key, value) => {
    setFilterState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [key]: value
      }
    }));
  }, []);

  const updateTermFilter = useCallback((key, value) => {
    setFilterState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [key]: value
      }
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState(createDefaultFilterState());
  }, []);

  const closeFilterPanel = useCallback(() => {
    setFilterOpen(false);
  }, []);

  const toggleFilterPanel = useCallback(() => {
    setFilterOpen(prev => !prev);
  }, []);

  const closePopover = useCallback(() => {
    popoverRef.current = null;
    setPopover(null);
    setPopoverPosition(null);
  }, []);

  const reloadCurrentView = useCallback(() => {
    filterGenerationRef.current += 1;
    fetchRef.current?.({ reset: false });
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden', background: '#000011' }}>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />

      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 15,
          p: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: 1.5,
          background: 'rgba(250, 250, 250, 0.94)'
        }}
      >
        <Chip label={`${cachedPointCount.toLocaleString()} cached sellers`} size="small" />
        {activeFilterCount > 0 && <Chip label={`${activeFilterCount} filters`} color="primary" size="small" />}
        {loadState.loading && <CircularProgress size={18} />}
        {loadState.error && <Chip label={loadState.error} color="error" size="small" />}
        <Tooltip title="Reload current view">
          <IconButton size="small" onClick={reloadCurrentView} aria-label="Reload current view">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Tooltip title="Toggle filters">
        <IconButton
          onClick={toggleFilterPanel}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 25,
            background: 'rgba(250, 250, 250, 0.94)',
            '&:hover': { background: '#fff' }
          }}
          aria-label="Toggle filters"
        >
          <FilterListIcon />
        </IconButton>
      </Tooltip>

      <Collapse in={filterOpen} unmountOnExit>
        <FilterPanel
          filterState={filterState}
          maxValues={maxValues}
          availableCategories={availableCategories}
          onEnableFilter={enableFilter}
          onDisableFilter={disableFilter}
          onUpdateRangeFilter={updateRangeFilter}
          onUpdateTermFilter={updateTermFilter}
          onClearFilters={clearFilters}
          onClose={closeFilterPanel}
        />
      </Collapse>

      {popover && popoverPosition && (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            left: popoverPosition.left,
            top: popoverPosition.top,
            zIndex: 30,
            width: { xs: 'calc(100vw - 32px)', sm: 430 },
            maxWidth: 430,
            p: 1.75,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(15, 23, 42, 0.12)',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.25)',
            transform: 'translate(-12px, -12px)'
          }}
          onClick={event => event.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={closePopover}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label="Close details"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {popover.type === 'seller'
            ? <SellerDetails seller={popover.seller} />
            : <RegionDetails region={popover} onOpenSeller={openSellerPopover} />}
        </Paper>
      )}
    </Box>
  );
};

const Metric = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const MetricCard = ({ accent, label, value }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: 1,
      background: '#fff',
      border: '1px solid #e5e7eb',
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
      borderTop: `3px solid ${accent}`
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 850, color: '#111827' }}>
      {value}
    </Typography>
  </Box>
);

const DetailLine = ({ icon, label, value }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: icon ? '24px 88px minmax(0, 1fr)' : '88px minmax(0, 1fr)',
      alignItems: 'center',
      gap: 0.75,
      p: 0.9,
      borderRadius: 1,
      background: '#f8fafc'
    }}
  >
    {icon && (
      <Box sx={{ display: 'flex', color: '#0f766e', '& svg': { fontSize: 18 } }}>
        {icon}
      </Box>
    )}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Box>
);

const FILTER_PANEL_SX = {
  position: 'absolute',
  top: 72,
  right: 20,
  width: { xs: 'calc(100vw - 40px)', sm: 340 },
  maxHeight: 'calc(100vh - 104px)',
  overflowY: 'auto',
  zIndex: 20,
  background: 'rgba(250, 250, 250, 0.96)',
  backdropFilter: 'blur(10px)',
  borderRadius: 1.5
};

const FilterPanel = React.memo(function FilterPanel({
  filterState,
  maxValues,
  availableCategories,
  onEnableFilter,
  onDisableFilter,
  onUpdateRangeFilter,
  onUpdateTermFilter,
  onClearFilters,
  onClose
}) {
  return (
    <Paper elevation={4} sx={FILTER_PANEL_SX}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close filters">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {RANGE_FILTERS.map(filter => {
            const enabled = filterState.enabled[filter.key];
            const currentValue = filterState.values[filter.key];
            const selectedMax = Array.isArray(currentValue) ? currentValue[1] : 0;
            const sliderMax = Math.max(
              1,
              DEFAULT_MAX_VALUES[filter.key],
              maxValues[filter.key],
              selectedMax || 0
            );
            const value = currentValue || [0, sliderMax];

            return (
              <Box key={filter.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">{filter.label}</Typography>
                  <Tooltip title={enabled ? 'Disable filter' : 'Enable filter'}>
                    <IconButton
                      size="small"
                      onClick={() => (enabled ? onDisableFilter(filter.key) : onEnableFilter(filter.key, sliderMax))}
                      color={enabled ? 'primary' : 'default'}
                      aria-label={`${enabled ? 'Disable' : 'Enable'} ${filter.label}`}
                    >
                      {enabled ? <CloseIcon fontSize="small" /> : <FilterListIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Slider
                  disabled={!enabled}
                  value={value}
                  onChange={(_, nextValue) => onUpdateRangeFilter(filter.key, nextValue)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={filter.valueLabel}
                  min={0}
                  max={sliderMax}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{filter.valueLabel(value[0])}</Typography>
                  <Typography variant="caption">{filter.valueLabel(value[1])}</Typography>
                </Box>
              </Box>
            );
          })}

          <Divider />

          <FormControl fullWidth size="small">
            <InputLabel>Main Categories</InputLabel>
            <Select
              multiple
              value={filterState.values.mainCategories}
              onChange={(event) => onUpdateTermFilter('mainCategories', event.target.value)}
              label="Main Categories"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => <Chip key={value} label={value} size="small" />)}
                </Box>
              )}
            >
              {availableCategories.mainCategories.map(category => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Subcategories</InputLabel>
            <Select
              multiple
              value={filterState.values.subcategories}
              onChange={(event) => onUpdateTermFilter('subcategories', event.target.value)}
              label="Subcategories"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => <Chip key={value} label={value} size="small" />)}
                </Box>
              )}
            >
              {availableCategories.subcategories.map(subcategory => (
                <MenuItem key={subcategory} value={subcategory}>
                  {subcategory}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" color="inherit" onClick={onClearFilters}>
            Clear All
          </Button>
        </Box>
      </Box>
    </Paper>
  );
});

const SellerDetails = React.memo(function SellerDetails({ seller }) {
  const fbaPercentage = clamp(Math.round(seller.fbaPercentage || 0), 0, 100);
  const monthGrowth = toNumber(seller.growth.monthOverMonthPercent, null);
  const locationText = [seller.city, seller.state, seller.country].filter(Boolean).join(', ') || 'Location unavailable';
  const categoryText = [seller.category.mainCategory, seller.category.subCategory].filter(Boolean).join(' / ') || 'Uncategorized';

  return (
    <Box>
      <Box
        sx={{
          mx: -0.75,
          mt: -0.75,
          mb: 1.75,
          p: 2,
          borderRadius: 1.5,
          color: '#fff',
          background: 'linear-gradient(135deg, #102033 0%, #0f766e 56%, #d97706 100%)',
          boxShadow: '0 18px 42px rgba(15, 32, 51, 0.28)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <StorefrontIcon sx={{ mt: 0.35 }} />
          <Box sx={{ minWidth: 0, pr: 3 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.08, fontWeight: 800 }}>
              {seller.sellerName}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.86 }}>
              {seller.businessName || seller.brandName || seller.amazonSellerId || 'Seller profile'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          <Chip
            size="small"
            label={seller.status.isSuspended ? 'Suspended' : 'Active'}
            sx={{ color: '#fff', background: seller.status.isSuspended ? 'rgba(220,38,38,0.72)' : 'rgba(34,197,94,0.24)' }}
          />
          {seller.brandName && (
            <Chip size="small" label={seller.brandName} sx={{ color: '#fff', background: 'rgba(255,255,255,0.18)' }} />
          )}
          {seller.sellerType && (
            <Chip size="small" label={seller.sellerType} sx={{ color: '#fff', background: 'rgba(255,255,255,0.18)' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <MetricCard accent="#0f766e" label="Monthly Revenue" value={formatCurrency(seller.monthlyRevenueUSD)} />
        <MetricCard accent="#d97706" label="Estimated Sales" value={formatCurrency(seller.estimatedSalesUSD)} />
        <MetricCard accent="#2563eb" label="Average Price" value={formatCurrency(seller.averagePriceUSD)} />
        <MetricCard accent="#7c3aed" label="Lifetime Reviews" value={formatNumber(seller.reviews.totalLifetime)} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          my: 1.5
        }}
      >
        <Box sx={{ p: 1.25, borderRadius: 1, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" color="text.secondary">FBA Share</Typography>
          <Box sx={{ height: 8, my: 0.75, borderRadius: 999, background: '#dbeafe', overflow: 'hidden' }}>
            <Box sx={{ width: `${fbaPercentage}%`, height: '100%', background: '#0284c7' }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>{fbaPercentage}%</Typography>
        </Box>

        <Box sx={{ p: 1.25, borderRadius: 1, background: '#fff7ed', border: '1px solid #fed7aa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 17, color: '#c2410c' }} />
            <Typography variant="caption" color="text.secondary">MoM Growth</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.8, fontWeight: 800, color: monthGrowth >= 0 ? '#166534' : '#b91c1c' }}>
            {monthGrowth === null ? '--' : `${monthGrowth.toFixed(1)}%`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
        <DetailLine icon={<LocationOnIcon />} label="Location" value={locationText} />
        <DetailLine icon={<StorefrontIcon />} label="Category" value={categoryText} />
        <DetailLine label="ASINs" value={`${formatNumber(seller.productStats.totalAsins)} total / ${formatNumber(seller.productStats.topAsins)} top`} />
        <DetailLine label="Amazon Seller ID" value={seller.amazonSellerId || '--'} />
        <DetailLine label="Last Updated" value={seller.lastUpdated ? new Date(seller.lastUpdated).toLocaleDateString() : '--'} />
      </Box>
    </Box>
  );
});

const RegionDetails = React.memo(function RegionDetails({ region, onOpenSeller }) {
  const totalCount = region.count ?? region.nearby.length;
  const headerTitle = region.cityName || 'Seller Cluster';
  const subtitle = region.cityName
    ? `${totalCount} sellers in this area`
    : `${totalCount} nearest cached sellers`;
  const showingNote = totalCount > region.nearby.length
    ? `Top ${region.nearby.length} by revenue`
    : null;

  return (
    <Box>
      <Typography variant="h6" sx={{ pr: 4 }}>
        {headerTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.5 }}>
        <Metric label="Cluster Revenue" value={formatCurrency(region.totalRevenue)} />
        <Metric label="Coordinates" value={`${region.lat.toFixed(2)}, ${region.lng.toFixed(2)}`} />
      </Box>

      {showingNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {showingNote}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {region.nearby.map(seller => (
          <Button
            key={seller.key}
            variant="text"
            color="inherit"
            onClick={() => onOpenSeller(seller)}
            sx={{
              justifyContent: 'space-between',
              textTransform: 'none',
              borderRadius: 1,
              px: 1
            }}
          >
            <span>{seller.sellerName}</span>
            <span>{formatCurrency(seller.monthlyRevenueUSD)}</span>
          </Button>
        ))}
      </Box>
    </Box>
  );
});

export default SellerGlobeApp;
